/*@flow*/
import * as t from 'lib/t';
import { Id, NormalizedNumber } from 'types/tcomb';
import enumList from './util/enumList';
import enumsFor from './util/enumsFor';
import mkTagTemplate from 'lib/templates/mkTagTemplate';
import isEmbedded from 'lib/isEmbedded';

const args = {
  id: t.maybe(Id).opt({ type: 'hidden' }),
  name: t.String,
  address: t.String.options({
    template: 'LocationPickerTemplate',
  }),
  mapLabel: t.maybe(t.String),
  theGeom: t.String.options({ type: 'hidden' }),
  status: t.String.options({
    template: 'ListingStatus',
    attrs: {
      placeholder: 'Choose status',
      classNamePrefix: 'Select',
    },
  }),
  visibility: enumsFor('visibility'),
  listingType: enumsFor('listingType').opt({
    nullOption: { value: '', text: 'Choose listing type' },
    categories: enumList.listingType,
  }),
  photoUrls: t.maybe(t.list(t.String)).opt({
    label: 'Photo(s)',
    template: 'PhotoUploaderTemplate',
    help: 'Photos must be larger than 600px wide.',
  }),
  documentsAttributes: t
    .maybe(t.list(t.struct({ name: t.String, url: t.String })))
    .opt({
      label: 'Additional Information & Supporting Documents',
      template: 'DocumentUploaderTemplate',
      help: 'Accepted file types: pdf, png, jpg.',
    }),
  availability: t.maybe(t.String),
  ceilingHeightFeet: t.maybe(NormalizedNumber).opt({
    label: 'Ceiling Height (ft)',
  }),
  frontageFeet: t.maybe(NormalizedNumber).opt({
    label: 'Frontage (ft)',
  }),
  closeDate: t.maybe(t.Date).opt({ label: 'Close Date' }),
  contactName: t.maybe(t.String).opt({
    before: {
      tag: 'h3',
      props: { children: 'Contact', className: 'mt-5' },
    },
    label: 'Name',
  }),
  contactCompany: t.maybe(t.String).opt({ label: 'Company' }),
  contactEmail: t.maybe(t.String).opt({ label: 'Email' }),
  phone: t.maybe(t.String).opt({
    label: 'Phone',
  }),
  secondaryContactName: t.maybe(t.String).opt({ label: 'Secondary Contact Name' }),
  secondaryContactCompany: t.maybe(t.String).opt({ label: 'Secondary Contact Company' }),
  secondaryContactEmail: t.maybe(t.String).opt({ label: 'Secondary Contact Email' }),
  secondaryContactPhone: t.maybe(t.String).opt({
    label: 'Secondary Contact Phone',
  }),
  description: t.maybe(t.String).opt({
    type: 'textarea',
    template: 'TextEditorTemplate',
  }),
  rentableSqft: t.maybe(NormalizedNumber).opt({
    label: 'Available Space (rentable sq ft)',
  }),
  intendedUse: t.list(enumsFor('propertyType')).opt({
    template: 'SelectTemplate',
    isMulti: true,
    label: 'Property Type',
    attrs: {
      placeholder: 'Choose property type',
      classNamePrefix: 'Select',
    },
    categories: enumList.propertyType,
  }),
  currentUses: t.maybe(t.list(enumsFor('use'))).opt({
    template: 'SelectTemplate',
    categories: enumList.use,
    isMulti: true,
    attrs: {
      placeholder: 'Enter the current use of listing',
      classNamePrefix: 'Select',
    },
  }),
  tags: t.maybe(t.list(t.String)).opt({
    template: mkTagTemplate('listings'),
    attrs: {
      placeholder: 'Select...',
      classNamePrefix: 'SelectTags',
    },
  }),
  notes: t
    .maybe(t.String)
    .opt({ type: 'textarea', template: 'TextEditorTemplate' }),
  url: t.maybe(t.String).opt({
    label: 'Website',
  }),
  email: t.maybe(t.String).opt({ label: "Email" }),
  incentives: t.maybe(t.String),
  pricePerSqft: t.maybe(NormalizedNumber).opt({
    label: 'Price per square foot (US $)',
  }),
  auxSpaceSqft: t.maybe(NormalizedNumber),
  marketValue: t.maybe(NormalizedNumber),

  contactIds: t.maybe(t.list(Id)).opt({
    before: {
      tag: 'h3',
      props: { children: 'Associated Records', className: 'mt-5' },
    },
    after: {
      tag: 'div',
      props: { className: 'mb-5 mt-5' },
    },
    template: 'Contacts',
    label: 'Contacts',
    attrs: {
      placeholder: 'Select Contact',
      classNamePrefix: 'SelectRecordContacts',
    },
    hideWhen: isEmbedded,
  }),
};

const order = [
  'name',
  'address',
  'theGeom',
  'mapLabel',
  'status',
  'markerColor',
  'visibility',
  'listingType',
  'intendedUse',
  'auxSpaceSqft',

  'marketValue',

  // For Lease
  'leaseType',
  'leaseTerm',
  'askingRentPerMonth',

  // For Sale
  'noi',
  'capRate',
  'askingPrice',

  'description',
  'photoUrls',
  'documentsAttributes',
  'currentUses',
  'use',
  'tags',
  'notes',
  'url',
  'email',
  'incentives',
  'pricePerSqft',
  'rentableSqft',
  'availability',
  'ceilingHeightFeet',
  'frontageFeet',
  'closeDate',
  'contactName',
  'contactCompany',
  'contactEmail',
  'phone',
  'secondaryContactName',
  'secondaryContactEmail',
  'secondaryContactPhone',
  'secondaryContactCompany',

  // Associated Records
  'contactIds',
];

const listing = t.struct(args, 'Listing').opt<any>({ legend: '', order });

export const forLease = listing.extend({
  leaseType: t.maybe(enumsFor('leaseType')).opt({
    nullOption: { value: '', text: 'Choose lease type' },
  }),
  leaseTerm: t.maybe(t.String),
  askingRentPerMonth: t.maybe(NormalizedNumber).opt({
    label: 'Asking Rent (per month)',
  }),
});

export const forSale = listing.extend({
  noi: t.maybe(NormalizedNumber),
  capRate: t.maybe(NormalizedNumber),
  askingPrice: t.maybe(NormalizedNumber).opt({
    label: 'Asking Price',
  }),
});

export const forLeaseAndSale = listing.extend({
  leaseType: t.maybe(enumsFor('leaseType')).opt({
    nullOption: { value: '', text: 'Choose lease type' },
  }),
  leaseTerm: t.maybe(t.String),
  askingRentPerMonth: t.maybe(NormalizedNumber).opt({
    label: 'Asking Rent (per month)',
  }),
  noi: t.maybe(NormalizedNumber),
  capRate: t.maybe(NormalizedNumber),
  askingPrice: t.maybe(NormalizedNumber).opt({
    label: 'Asking Price',
  }),
});

export default listing;
